import { useState, useEffect } from 'react';
import { Box, chakra, Flex } from '@chakra-ui/react';
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { HexColorPicker } from 'react-colorful';
import { InputField } from './input';

export interface ColorPickerInputProps {
  name?: string;
  value?: string;
  onChange?: (color: string) => void;
  placeholder?: string;
  disabled?: boolean;
  presetColors?: string[];
  id?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
  /** Accessible name for the hex input. When a visible label
   *  points at this component's `id`, pass a name that CONTAINS that
   *  label's text (WCAG 2.5.3) — aria-label overrides label-by-htmlFor
   *  in accessible-name computation. */
  'aria-label'?: string;
}

/**
 * @useFor a color-picker field inside a `<Field.Root>` — swatch
 *   trigger + popover with preset palette + free-form hex input.
 *
 *   Examples in-tree: AlertType color, District / School branding
 *   colors, tag color in Roles.
 *
 *   For status-color displays (read-only, semantic) use
 *   `<StatusBadge>` — its color palette is canonical, not
 *   user-chosen. ColorPickerInput is for user-chosen colors where
 *   any value in the brand palette is acceptable.
 */
export function ColorPickerInput({
  id,
  name,
  value = '',
  onChange,
  placeholder = '#000000',
  disabled = false,
  presetColors,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  'aria-label': ariaLabel = 'Color hex value',
}: ColorPickerInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const isValidHex = (hex: string) => /^#[0-9A-Fa-f]{6}$/.test(hex);

  const normalizeHex = (raw: string): string => {
    const trimmed = raw.trim();
    const hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return hex;
  };

  const handlePickerChange = (color: string) => {
    setInputValue(color);
    onChange?.(color);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);

    const hex = normalizeHex(raw);
    if (isValidHex(hex)) {
      onChange?.(hex);
    }
  };

  const handleInputBlur = () => {
    const hex = normalizeHex(inputValue);
    if (isValidHex(hex)) {
      setInputValue(hex);
      onChange?.(hex);
    }
  };

  const displayColor = isValidHex(normalizeHex(inputValue))
    ? normalizeHex(inputValue)
    : '#cccccc';

  return (
    <Flex gap={2} alignItems="center">
      <PopoverRoot
        positioning={{ placement: 'bottom-start' }}
        open={isOpen}
        onOpenChange={(details) => {
          if (!disabled) setIsOpen(details.open);
        }}
      >
        <PopoverTrigger asChild>
          <chakra.button
            type="button"
            w="40px"
            h="40px"
            minW="40px"
            borderRadius="md"
            border="1px solid"
            borderColor="border.secondary"
            bg={displayColor}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            opacity={disabled ? 0.5 : 1}
            _hover={disabled ? {} : { borderColor: 'border.primary' }}
            aria-label="Pick a color"
          />
        </PopoverTrigger>
        <PopoverContent w="auto" p={0}>
          <PopoverBody p={3}>
            <Box
              css={{
                '& .react-colorful': {
                  width: '200px',
                  height: '200px',
                },
              }}
            >
              <HexColorPicker
                color={displayColor}
                onChange={handlePickerChange}
              />
            </Box>

            {presetColors && presetColors.length > 0 && (
              <Flex mt={2} gap={1} flexWrap="wrap">
                {presetColors.map((preset) => (
                  <chakra.button
                    key={preset}
                    type="button"
                    w="24px"
                    h="24px"
                    borderRadius="sm"
                    border="1px solid"
                    borderColor="border.secondary"
                    bg={preset}
                    cursor="pointer"
                    _hover={{ borderColor: 'border.primary', transform: 'scale(1.1)' }}
                    onClick={() => handlePickerChange(preset)}
                    aria-label={`Select color ${preset}`}
                  />
                ))}
              </Flex>
            )}
          </PopoverBody>
        </PopoverContent>
      </PopoverRoot>

      <InputField
        id={id}
        aria-label={ariaLabel}
        name={name}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        disabled={disabled}
        maxW="140px"
        fontFamily="mono"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
      />
    </Flex>
  );
}
